/**
 * Compact brand quick-switch popover for the sidebar's bottom-left button.
 *
 * A "small div" version of the full SwitchShop popup: a short search box + a
 * scrollable list of the user's brands (`all_shops`), the active one marked,
 * click-to-switch. It reuses the EXACT SwitchShop switch mechanism —
 * `updateProfile({ active_shop_slug })` → `navigate(HOME_ROUTE)` →
 * `fetchAppInit(true)` — so the whole app re-scopes globally. A "See all" link
 * at the bottom opens the full Brands page (`/clients`).
 */
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RootState } from '../../store/store';
import { updateProfile } from '../../store/reducers/userSlice';
import { fetchAppInit } from '../../lib/api/app-init';
import { HOME_ROUTE } from '../../lib/constants/route';
import { arrowRightThinSVG } from '../../lib/allCustomSVGs';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import '../../styles/components/sidebar/switchShop.scss';

interface Props {
  /** Close the popover (parent owns the open/closed flag). */
  onClose: () => void;
}

const BrandQuickSwitch = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userProfile = useSelector((state: RootState) => state.user.profile);
  const allShops = userProfile?.all_shops || [];
  const activeShopSlug = userProfile?.active_shop_slug;

  const [searchQuery, setSearchQuery] = useState('');
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingShopSlug, setSwitchingShopSlug] = useState<string | null>(
    null
  );

  // Filter by name, then float the active brand to the top.
  const filteredShops = useMemo(() => {
    const list = !searchQuery.trim()
      ? allShops
      : allShops.filter((shop) =>
          shop.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    const active = list.find((s) => s.slug === activeShopSlug);
    const others = list.filter((s) => s.slug !== activeShopSlug);
    return active ? [active, ...others] : list;
  }, [allShops, searchQuery, activeShopSlug]);

  const handleShopSwitch = (shopSlug: string) => {
    if (isSwitching || shopSlug === activeShopSlug) return;
    setIsSwitching(true);
    setSwitchingShopSlug(shopSlug);
    // Same global switch mechanism as SwitchShop: set the active brand,
    // navigate home, then re-hydrate the whole app for the new brand.
    setTimeout(() => {
      try {
        dispatch(updateProfile({ active_shop_slug: shopSlug }));
        navigate(HOME_ROUTE);
        fetchAppInit(true);
      } catch (error) {
        console.error('Error switching shop:', error);
      } finally {
        onClose();
      }
    }, 600);
  };

  const handleSeeAll = () => {
    onClose();
    navigate('/clients');
  };

  return (
    <motion.div
      className='brandQuickSwitch'
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.15 }}
    >
      <div className='brandQuickSwitch__search'>
        <input
          type='text'
          autoFocus
          className='brandQuickSwitch__search-input'
          placeholder={t('SwitchShop-search-placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={isSwitching}
        />
      </div>

      <div className='brandQuickSwitch__list'>
        {filteredShops.length === 0 ? (
          <div className='brandQuickSwitch__empty'>
            {t('SwitchShop-no-results', { query: searchQuery })}
          </div>
        ) : (
          filteredShops.map((shop) => {
            const isActive = shop.slug === activeShopSlug;
            const isThisSwitching =
              isSwitching && switchingShopSlug === shop.slug;
            return (
              <button
                key={shop.id}
                type='button'
                className={`brandQuickSwitch__item${
                  isActive ? ' brandQuickSwitch__item--active' : ''
                }`}
                onClick={() => handleShopSwitch(shop.slug)}
                disabled={isSwitching}
                title={shop.name}
              >
                <span className='brandQuickSwitch__avatar'>
                  {shop.name.charAt(0).toUpperCase()}
                </span>
                <span className='brandQuickSwitch__name'>{shop.name}</span>
                {isThisSwitching ? (
                  <span className='brandQuickSwitch__spinner' />
                ) : isActive ? (
                  <span className='brandQuickSwitch__check'>✓</span>
                ) : null}
              </button>
            );
          })
        )}
      </div>

      <button
        type='button'
        className='brandQuickSwitch__see-all'
        onClick={handleSeeAll}
        disabled={isSwitching}
      >
        <span>{t('SwitchShop-see-all', 'See all')}</span>
        <CustomSVGs svg={arrowRightThinSVG} />
      </button>
    </motion.div>
  );
};

export default BrandQuickSwitch;
